/**
 * Minimal popover hook built on the native popover API.
 *
 * `popover="auto"` light-dismisses on outside click / Esc, but the
 * dismissing click passes through to the element behind it. A dedicated
 * backdrop layer swallows those clicks so they never reach the page
 * (e.g. opening a news card behind the popover).
 */
import Bunnix, { Show, useRef, useState } from "@bunnix/core";
import { Column } from "@bunnix/components";
import "./usePopover.css";

const { div } = Bunnix;

const resolveWidth = (value) =>
  typeof value === "number" ? `${value}px` : value;

/**
 * Creates a popover controller and render component.
 *
 * @returns {{
 *   Popover: Function,
 *   showPopover: Function,
 *   hidePopover: Function
 * }} Popover renderer and imperative open/close handlers
 */
export default function usePopover() {
  const popoverState = useState({ contents: [], width: null });
  const backdropVisible = useState(false);
  const popoverRef = useRef(null);

  const hidePopover = () => {
    try {
      popoverRef.current?.hidePopover();
    } catch {
      // Already closed: just sync the backdrop below.
    }
    backdropVisible.set(false);
  };

  const handleToggle = (event) => {
    if (event?.newState === "closed") backdropVisible.set(false);
  };

  return {
    /**
     * Popover container bound to this hook instance state.
     *
     * @returns {*} Renderable popover node
     */
    Popover: () =>
      div(
        { class: "popover-root" },
        Show(
          backdropVisible,
          div({ class: "popover-backdrop", click: hidePopover }),
        ),
        Show(popoverState, (state) =>
          div(
            {
              ref: popoverRef,
              popover: "auto",
              class: "popover",
              toggle: handleToggle,
              style: {
                ...(state.width ? { width: resolveWidth(state.width) } : {}),
              },
            },
            Column({ padding: "regular" }, ...state.contents),
          ),
        ),
      ),

    /**
     * Opens the popover with the provided content.
     *
     * @param {Object} [config] - Popover config
     * @param {Array|*} [config.contents=[]] - Renderable content block(s)
     * @param {number|string|null} [config.width=null] - Fixed popover width
     * @returns {void}
     */
    showPopover: ({ contents = [], width = null } = {}) => {
      popoverState.set({
        contents: Array.isArray(contents) ? contents : [contents],
        width,
      });
      backdropVisible.set(true);
      try {
        popoverRef.current?.showPopover();
      } catch {
        // Already open: content was updated, nothing else to do.
      }
    },

    /**
     * Closes the popover and removes the backdrop.
     *
     * @returns {void}
     */
    hidePopover,
  };
}
